import { getFunctions, connectFunctionsEmulator, httpsCallable, type HttpsCallableResult, type Functions } from 'firebase/functions';
import Constants from 'expo-constants';
import { firebaseApp } from './firebase';
import { Platform } from 'react-native';


const region = 'us-central1';

// Check emulator configuration - respect explicit false settings (highest priority)
// Config file (app.json) takes ABSOLUTE precedence over environment variables
const useFunctionsEmulatorConfig = Constants.expoConfig?.extra?.useFunctionsEmulator;
const emulatorExplicitlyDisabled = useFunctionsEmulatorConfig === false;
const useEmulatorFromConfig = useFunctionsEmulatorConfig === true;


// Environment variable (lower priority - only used if config doesn't explicitly say true or false)
// If config explicitly says true, ignore env var. If config explicitly says false, respect it.
// Only use env var if config is undefined/null
const useEmulatorFromEnv = useFunctionsEmulatorConfig === undefined && process.env.EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR === 'true';
const envVarExplicitlyDisabled = useFunctionsEmulatorConfig === undefined && process.env.EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR === 'false';
const emulatorHostFromEnv = process.env.EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST || Constants.expoConfig?.extra?.functionsEmulatorHost;
const emulatorHostProvided = typeof emulatorHostFromEnv === 'string' && emulatorHostFromEnv.length > 0;

// Config file setting takes ABSOLUTE precedence
// If config says true, use emulator regardless of env var
// If config says false, don't use emulator regardless of env var
// Only check env var if config is undefined
let shouldUseEmulator = false;
if (emulatorExplicitlyDisabled || envVarExplicitlyDisabled) {
  // Explicit opt-out
  shouldUseEmulator = false;
} else if (useEmulatorFromConfig || useEmulatorFromEnv) {
  // Explicit opt-in
  shouldUseEmulator = __DEV__;
} else {
  // Default to production even in dev.
  // Emulators must be explicitly enabled via app.json (extra.useFunctionsEmulator=true)
  // or EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR=true.
  shouldUseEmulator = false;
}

// Guardrail: never use emulators for staging/preview builds.
const appEnv = Constants.expoConfig?.extra?.appEnv;
if (appEnv === 'staging' || appEnv === 'preview') {
  shouldUseEmulator = false;
}

// Debug logging
if (__DEV__) {
  console.log('[Functions] Debug info:', {
    isDev: __DEV__,
    useEmulatorFromConfig,
    useEmulatorFromEnv,
    emulatorExplicitlyDisabled,
    envVarExplicitlyDisabled,
    shouldUseEmulator,
    appEnv,
    platform: Platform.OS,
    emulatorHostProvided,
    emulatorHostFromEnv,
    expoConfigExtra: Constants.expoConfig?.extra,
  });

  if (shouldUseEmulator && Platform.OS === 'ios') {
    console.log('[Functions] ⚠️  iOS Physical Device Checklist:');
    console.log('   1. Device and computer on same WiFi network');
    console.log('   2. Computer firewall allows port 5001');
    console.log(`   3. Emulator host: ${Constants.expoConfig?.extra?.functionsEmulatorHost}`);
    console.log('   4. Test connectivity: http://<HOST>:5001 from device browser');
  }
}

const parseHostPort = (value?: string) => {
  if (!value) return null;
  const [host, portStr] = value.split(':');
  const port = Number(portStr);
  if (!host || Number.isNaN(port)) return null;
  return { host, port };
};

const inferLanHostFromExpo = (): string | null => {
  try {
    // In Expo Go/dev, this is often available and includes the LAN IP.
    const candidates: Array<string | undefined> = [
      (Constants as any)?.expoConfig?.hostUri,
      (Constants as any)?.manifest?.debuggerHost,
      (Constants as any)?.manifest2?.extra?.expoClient?.debuggerHost,
      (Constants as any)?.manifest2?.extra?.expoGo?.debuggerHost,
    ];

    for (const raw of candidates) {
      if (!raw || typeof raw !== 'string') continue;
      // Strip scheme if present and drop any path
      const noScheme = raw.replace(/^[a-z]+:\/\//i, '');
      const hostPort = noScheme.split('/')[0];
      const host = hostPort.split(':')[0];
      if (host && host !== 'localhost' && host !== '127.0.0.1') {
        return host;
      }
    }
  } catch {
    // ignore
  }

  return null;
};

// Create functions instance AFTER determining if we should use emulator
// This ensures we don't accidentally connect to emulator if it's disabled
let functions = getFunctions(firebaseApp, region);

if (shouldUseEmulator) {
  // Connect to emulator for local development
  // Make sure to run: firebase emulators:start --only functions
  try {
    const hostPort =
      parseHostPort(process.env.EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST) ||
      parseHostPort(Constants.expoConfig?.extra?.functionsEmulatorHost) ||
      (() => {
        if (Platform.OS === 'web') return null;
        const inferredHost = inferLanHostFromExpo();
        return inferredHost ? { host: inferredHost, port: 5001 } : null;
      })() || { host: 'localhost', port: 5001 };

    if (Platform.OS !== 'web' && hostPort.host === 'localhost') {
      console.warn(
        '[Functions] Using localhost for emulator. On a physical device, set EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST to your LAN IP (e.g., 192.168.x.x:5001).'
      );
    }

    connectFunctionsEmulator(functions, hostPort.host, hostPort.port);
    console.log(`✅ Connected to Firebase Functions emulator at ${hostPort.host}:${hostPort.port}`);
    if (Platform.OS === 'web') {
      console.log('🌐 Using Firebase Functions emulator for web development');
    }
  } catch (error) {
    console.warn('⚠️ Failed to connect to Firebase Functions emulator.');
    console.warn('   Make sure emulators are running with: firebase emulators:start');
    console.warn('   Falling back to production Firebase Functions.');
    console.warn('   To disable emulator auto-connection, set useFunctionsEmulator: false in app.json');
    // Recreate functions instance to avoid using the failed emulator connection
    functions = getFunctions(firebaseApp, region);
    console.log('📡 Using production Firebase Functions.');
  }
} else {
  // Explicitly ensure we're using production functions
  // Create a fresh instance to avoid any cached emulator connections
  functions = getFunctions(firebaseApp, region);
  if (__DEV__) {
    console.log('📡 Using production Firebase Functions.');
  }
}

export function httpsCallableWithTimeout<RequestData = unknown, ResponseData = unknown>(
  functionsInstance: Functions,
  name: string,
  timeoutMs: number = 60000,
) {
  const callable = httpsCallable<RequestData, ResponseData>(functionsInstance, name);

  return async (data: RequestData): Promise<HttpsCallableResult<ResponseData>> => {
    let timer: NodeJS.Timeout | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        const emulatorHostHint =
          Constants.expoConfig?.extra?.functionsEmulatorHost ||
          process.env.EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST ||
          'localhost:5001';

        reject(
          new Error(
            shouldUseEmulator
              ? `Function ${name} timed out after ${timeoutMs}ms. If testing on a physical device with emulators, ensure your device can reach ${emulatorHostHint}.`
              : `Function ${name} timed out after ${timeoutMs}ms. Please check your network connection and try again.`,
          ),
        );
      }, timeoutMs);
    });

    try {
      return await Promise.race([callable(data), timeoutPromise]);
    } catch (error: any) {
      throw error;
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
    }
  };
}

export { functions, httpsCallable };


